// =============================================================================
// VandieZ admin-web -- theme + sidebar-collapse providers
// =============================================================================
// Two small contexts that any component can consume:
//
//   useTheme()      -> { theme: 'light'|'dark', setTheme, toggle }
//   useCollapsed()  -> { collapsed: boolean, setCollapsed, toggle }
//
// Both persist to localStorage so a refresh remembers the operator's
// choice. The theme provider also toggles the `.dark` class on
// <html>, which flips every CSS variable in index.css (the shadcn
// palette is already dark-mode-ready).
// =============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type React from "react";

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------
export type Theme = "light" | "dark";
const THEME_KEY = "vandiez-admin-theme";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): Theme {
  if (typeof window === "undefined") {return "light";}
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored === "dark" || stored === "light") {return stored;}
  } catch {
    // ignore
  }
  // Fall back to OS preference.
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

export function ThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  // Apply the class + persist whenever theme changes.
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => { setThemeState(t); }, []);
  const toggle = useCallback(() => {
    setThemeState((cur) => (cur === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggle }),
    [theme, setTheme, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Sidebar collapse
// ---------------------------------------------------------------------------
const COLLAPSE_KEY = "vandiez-admin-sidebar-collapsed";

interface CollapseContextValue {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  toggle: () => void;
}

const CollapseContext = createContext<CollapseContextValue | null>(null);

function readInitialCollapsed(): boolean {
  if (typeof window === "undefined") {return false;}
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

export function SidebarCollapseProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [collapsed, setCollapsedState] = useState<boolean>(readInitialCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      // ignore
    }
  }, [collapsed]);

  const setCollapsed = useCallback(
    (v: boolean) => { setCollapsedState(v); },
    [],
  );
  const toggle = useCallback(() => {
    setCollapsedState((c) => !c);
  }, []);

  const value = useMemo(
    () => ({ collapsed, setCollapsed, toggle }),
    [collapsed, setCollapsed, toggle],
  );

  return (
    <CollapseContext.Provider value={value}>
      {children}
    </CollapseContext.Provider>
  );
}

export function useCollapsed(): CollapseContextValue {
  const ctx = useContext(CollapseContext);
  if (ctx === null) {
    throw new Error(
      "useCollapsed must be used inside <SidebarCollapseProvider>",
    );
  }
  return ctx;
}
